// לשונית "תזמונים" במסך יצירה (19 §1.5): רשימת התזמונים של המשתמש על היצירה (admin — של כולם) + טופס "תזמון חדש".
// השרת הוא המקור: describeHe, הפעלה הבאה, מצב ההשהיה ואזהרת ממשקי 'אישור' (1.1) — המסך מציג ושולח, לא מחשב.
import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api.ts';
import { lookupSummary } from '../lib/schemaForm.ts';
import { T } from '../lib/strings.ts';
import { fmtWhen } from '../lib/time.ts';
import { cn } from '../lib/utils.ts';
import { presetCron, triggersApi, type Preset, type TriggerView, type TriggerWarning } from '../lib/triggers.ts';
import { EmptyState, ErrorView, Loading } from './Status.tsx';
import { ParamsForm } from './ParamsForm.tsx';
import { StatusLabel, runTone } from './ui/badge.tsx';
import { Button } from './ui/button.tsx';
import { Card } from './ui/card.tsx';
import { Input, Label, Select } from './ui/input.tsx';
import { IconClock, IconPlay } from './brand/icons.tsx';

const errText = (e: unknown) => (e instanceof ApiError ? e.textHe : T.network);
const when = (iso: string | null) => (iso ? <bdi dir="auto" className="tabular-nums">{fmtWhen(iso)}</bdi> : '–');

/** אזהרות 1.1 (ממשק במצב 'אישור') עם קישור ללשונית הממשקים. */
function Warnings({ warnings, interfacesHref }: { warnings: TriggerWarning[]; interfacesHref: string | null }) {
  if (!warnings.length) return null;
  return (
    <div role="status" className="flex flex-col gap-2 rounded-md border border-degraded/40 bg-degraded/[0.07] p-3 text-sm" data-testid="trigger-warnings">
      {warnings.map((w) => (
        <p key={w.interfaceId} className="m-0" data-testid="trigger-warning" data-iface={w.interfaceId}>
          {w.textHe}
        </p>
      ))}
      {interfacesHref && (
        <Link to={interfacesHref} replace className="inline-flex min-h-11 w-fit items-center font-semibold text-primary underline-offset-4 hover:underline" data-testid="trigger-warning-link">
          {T.triggers.toInterfaces}
        </Link>
      )}
    </div>
  );
}

function Switch({ checked, onChange, disabled, label }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className="inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center disabled:opacity-50"
      data-testid="trigger-switch"
    >
      <span className={cn('relative inline-flex h-6 w-11 rounded-circle transition-colors', checked ? 'bg-ok' : 'bg-muted-foreground/40')}>
        <span className={cn('absolute top-0.5 size-5 rounded-circle bg-card shadow transition-[inset-inline-start]', checked ? 'start-[1.375rem]' : 'start-0.5')} />
      </span>
    </button>
  );
}

function Row({ t, slug, interfacesHref, paramsSchema }: { t: TriggerView; slug: string; interfacesHref: string | null; paramsSchema: unknown }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<TriggerWarning[]>([]);
  const [confirm, setConfirm] = useState(false);
  const active = t.enabled && !t.pausedReason;
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['triggers', slug] }),
      qc.invalidateQueries({ queryKey: ['runs', slug] }),
      qc.invalidateQueries({ queryKey: ['artifacts'] }),
    ]);
  const act = async (f: () => Promise<{ summaryHe?: string; warnings?: TriggerWarning[] } | void>) => {
    setBusy(true);
    setErr(null);
    setNote(null);
    try {
      const r = await f();
      if (r) {
        setNote(r.summaryHe || null);
        setWarnings(r.warnings ?? []);
      }
      await refresh();
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  };
  // מתג: מושהה מסיבה (pausedReason) → resume (בודק מחדש ומאפס כשלונות); אחרת PATCH enabled
  const toggle = (on: boolean) => act(() => (on ? (t.pausedReason ? triggersApi.resume(t.id) : triggersApi.update(t.id, { enabled: true })) : triggersApi.update(t.id, { enabled: false })));
  return (
    <li className="flex flex-col gap-2 rounded-md border border-border bg-card p-3 shadow-card" data-testid="trigger-row" data-id={t.id} data-active={active ? '1' : '0'}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <div className="me-auto flex min-w-0 flex-col">
          <span className="font-semibold" data-testid="trigger-name">
            {t.nameHe}
          </span>
          <span className="text-sm text-muted-foreground" data-testid="trigger-describe">
            {t.describeHe}
            {t.owner && <> · {T.triggers.by(t.owner.nameHe)}</>}
          </span>
          {/* artifacts-report-lookup: "לקוח: <תווית>" */}
          {lookupSummary(paramsSchema, t.params, t.paramLabels) && (
            <bdi dir="auto" className="text-sm" data-testid="trigger-params">
              {lookupSummary(paramsSchema, t.params, t.paramLabels)}
            </bdi>
          )}
        </div>
        {t.pausedReason ? (
          <StatusLabel tone="error" data-testid="trigger-paused">
            {T.triggers.paused}
          </StatusLabel>
        ) : (
          <StatusLabel tone={t.enabled ? 'ok' : 'neutral'} data-testid="trigger-state">
            {t.enabled ? T.triggers.active : T.triggers.off}
          </StatusLabel>
        )}
        <Switch checked={active} disabled={busy} onChange={(v) => void toggle(v)} label={`${t.nameHe}: ${active ? T.triggers.active : T.triggers.off}`} />
      </div>
      {t.pausedReason && t.pausedHe && (
        <p className="m-0 text-sm text-error" data-testid="trigger-paused-reason">
          {T.triggers.paused}: {t.pausedHe}
        </p>
      )}
      <dl className="m-0 grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
        <div className="flex flex-wrap gap-x-2">
          <dt className="text-muted-foreground">{T.triggers.next}:</dt>
          <dd className="m-0" data-testid="trigger-next">
            {when(t.nextRunAt)}
          </dd>
        </div>
        <div className="flex flex-wrap items-center gap-x-2">
          <dt className="text-muted-foreground">{T.triggers.last}:</dt>
          <dd className="m-0 flex flex-wrap items-center gap-2" data-testid="trigger-last">
            {t.lastRunAt ? (
              <>
                {when(t.lastRunAt)}
                {t.lastStatus &&
                  (t.lastRunId ? (
                    <Link to={`/artifacts/${slug}/runs/${t.lastRunId}`} className="inline-flex min-h-11 items-center" data-testid="trigger-last-run">
                      <StatusLabel tone={runTone(t.lastStatus)}>
                        {T.artifact.status[t.lastStatus] ?? t.lastStatus}
                      </StatusLabel>
                    </Link>
                  ) : (
                    <StatusLabel tone={runTone(t.lastStatus)}>
                      {T.artifact.status[t.lastStatus] ?? t.lastStatus}
                    </StatusLabel>
                  ))}
              </>
            ) : (
              T.triggers.never
            )}
          </dd>
        </div>
      </dl>
      {t.lastErrorHe && <p className="m-0 text-sm text-error">{t.lastErrorHe}</p>}
      {t.retryHe && (
        <p className="m-0 text-sm text-muted-foreground" data-testid="trigger-retry">
          {t.retryHe}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" disabled={busy} onClick={() => void act(() => triggersApi.run(t.id))} data-testid="trigger-run-now">
          <IconPlay size={14} className="fill-current" />
          {T.triggers.runNow}
        </Button>
        {confirm ? (
          <>
            <span className="text-sm">{T.triggers.confirmRemove(t.nameHe)}</span>
            <Button variant="destructive" size="sm" disabled={busy} onClick={() => void act(() => triggersApi.remove(t.id))} data-testid="trigger-remove-yes">
              {T.triggers.remove}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setConfirm(false)}>
              {T.artifact.cancel}
            </Button>
          </>
        ) : (
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => setConfirm(true)} data-testid="trigger-remove">
            {T.triggers.remove}
          </Button>
        )}
      </div>
      {note && (
        <p role="status" className="m-0 text-sm text-ok" data-testid="trigger-note">
          {warnings.length ? note.split('; שים לב:')[0] : note}
        </p>
      )}
      <Warnings warnings={warnings} interfacesHref={interfacesHref} />
      {err && (
        <p role="alert" className="m-0 text-sm text-error" data-testid="trigger-error">
          {err}
        </p>
      )}
    </li>
  );
}

const PRESETS: Preset[] = ['daily', 'workdays', 'weekly', 'monthly', 'advanced'];

function NewTrigger({ slug, defaultName, paramsSchema, interfacesHref }: { slug: string; defaultName: string; paramsSchema: unknown; interfacesHref: string | null }) {
  const qc = useQueryClient();
  const [nameHe, setName] = useState(defaultName);
  const [preset, setPreset] = useState<Preset>('daily');
  const [time, setTime] = useState('07:00');
  const [weekday, setWeekday] = useState(0);
  const [dom, setDom] = useState(1);
  const [cron, setCron] = useState('0 7 * * *');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [saved, setSaved] = useState<{ summaryHe: string; warnings: TriggerWarning[] } | null>(null);
  const [formKey, setFormKey] = useState(0);

  const save = async (params: Record<string, unknown>, paramLabels?: Record<string, string>) => {
    setBusy(true);
    setErr(null);
    setSaved(null);
    try {
      const expr = preset === 'advanced' ? cron : presetCron(preset, time, weekday, dom);
      const r = await triggersApi.create({ nameHe: nameHe.trim() || defaultName, slug, params, ...(paramLabels ? { paramLabels } : {}), schedule: { kind: 'cron', expr } });
      setSaved({ summaryHe: r.summaryHe, warnings: r.warnings });
      setFormKey((k) => k + 1);
      await Promise.all([qc.invalidateQueries({ queryKey: ['triggers', slug] }), qc.invalidateQueries({ queryKey: ['artifacts'] })]);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="flex flex-col gap-4 p-4 md:p-5" data-testid="trigger-new">
      <h2 className="m-0 flex items-center gap-2 text-base font-semibold">
        <IconClock size={18} className="text-primary" />
        {T.triggers.newTitle}
      </h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="flex flex-col gap-1 sm:col-span-2">
          <Label htmlFor="trg-name">{T.triggers.name}</Label>
          <Input id="trg-name" dir="auto" value={nameHe} maxLength={120} onChange={(e) => setName(e.target.value)} data-testid="trigger-name-input" />
        </div>
        <div className="flex flex-col gap-1 sm:col-span-2">
          <Label htmlFor="trg-preset">{T.triggers.when}</Label>
          <Select id="trg-preset" value={preset} onChange={(e) => setPreset(e.target.value as Preset)} data-testid="trigger-preset">
            {PRESETS.map((p) => (
              <option key={p} value={p}>
                {T.triggers.presets[p]}
              </option>
            ))}
          </Select>
        </div>
        {preset === 'advanced' ? (
          <div className="flex flex-col gap-1 sm:col-span-2">
            <Label htmlFor="trg-cron">{T.triggers.cron}</Label>
            <Input id="trg-cron" dir="ltr" className="font-mono" value={cron} onChange={(e) => setCron(e.target.value)} data-testid="trigger-cron" />
          </div>
        ) : (
          <>
            {preset === 'weekly' && (
              <div className="flex flex-col gap-1">
                <Label htmlFor="trg-dow">{T.triggers.weekday}</Label>
                <Select id="trg-dow" value={weekday} onChange={(e) => setWeekday(Number(e.target.value))} data-testid="trigger-weekday">
                  {T.triggers.days.map((d, i) => (
                    <option key={d} value={i}>
                      {d}
                    </option>
                  ))}
                </Select>
              </div>
            )}
            {preset === 'monthly' && (
              <div className="flex flex-col gap-1">
                <Label htmlFor="trg-dom">{T.triggers.dom}</Label>
                <Select id="trg-dom" value={dom} onChange={(e) => setDom(Number(e.target.value))} data-testid="trigger-dom">
                  {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </Select>
              </div>
            )}
            <div className="flex flex-col gap-1">
              <Label htmlFor="trg-time">{T.triggers.time}</Label>
              <Input id="trg-time" type="time" dir="ltr" step={300} value={time} onChange={(e) => setTime(e.target.value)} data-testid="trigger-time" />
            </div>
          </>
        )}
      </div>
      <ParamsForm
        key={formKey}
        schema={paramsSchema}
        busy={busy}
        onSubmit={(p, _v, labels) => void save(p, labels)}
        lookup={(param, q) => api.lookup(slug, param, q)}
        submit={{ label: T.triggers.save, busyLabel: T.triggers.saving, testId: 'trigger-save' }}
      />
      {err != null && <ErrorView error={err} />}
      {saved && (
        <div className="flex flex-col gap-2">
          <p role="status" className="m-0 text-sm text-ok" data-testid="trigger-saved">
            {/* האזהרות מוצגות בנפרד (עם הקישור) — בלי לחזור עליהן בסיכום */}
            {(saved.warnings.length ? saved.summaryHe.split('; שים לב:')[0] : saved.summaryHe) || T.triggers.saved}
          </p>
          <Warnings warnings={saved.warnings} interfacesHref={interfacesHref} />
        </div>
      )}
    </Card>
  );
}

export function TriggersTab({ slug, artifactNameHe, paramsSchema, interfacesHref }: { slug: string; artifactNameHe: string; paramsSchema: unknown; interfacesHref: string | null }) {
  const q = useQuery({ queryKey: ['triggers', slug], queryFn: () => triggersApi.forArtifact(slug) });
  return (
    <div className="flex flex-col gap-4" data-testid="triggers-tab">
      <p className="m-0 text-sm text-muted-foreground">{T.triggers.hint}</p>
      {q.isPending ? (
        <Loading />
      ) : q.isError ? (
        <ErrorView error={q.error} onRetry={() => q.refetch()} />
      ) : q.data.triggers.length ? (
        <ul className="m-0 flex list-none flex-col gap-2 p-0" data-testid="triggers-list">
          {q.data.triggers.map((t) => (
            <Row key={t.id} t={t} slug={slug} interfacesHref={interfacesHref} paramsSchema={paramsSchema} />
          ))}
        </ul>
      ) : (
        <EmptyState icon={<IconClock size={22} />} title={T.triggers.none} />
      )}
      <NewTrigger slug={slug} defaultName={artifactNameHe} paramsSchema={paramsSchema} interfacesHref={interfacesHref} />
    </div>
  );
}
